<template>
    <div class="widget-component" v-if="renderComponent">
        <v-container fluid class="widget-listing-slider">
            <v-row class="mb-6">
                <v-col cols="12" v-if="normalizedOptions.type == 'slick'">
                    <VueSlickCarousel
                        :accessibility="normalizedOptions.slickOptions.accessibility"
                        :adaptiveHeight:="normalizedOptions.slickOptions.adaptiveHeight"
                        :arrows="normalizedOptions.slickOptions.arrows"
                        :asNavFor="normalizedOptions.slickOptions.asNavFor"
                        :autoplay="normalizedOptions.slickOptions.autoplay"
                        :autoplaySpeed="normalizedOptions.slickOptions.autoplaySpeed"
                        :centerMode="normalizedOptions.slickOptions.centerMode"
                        :centerPadding="normalizedOptions.slickOptions.centerPadding"
                        :cssEase="normalizedOptions.slickOptions.cssEase"
                        :dots="normalizedOptions.slickOptions.dots"
                        :dotsClass="normalizedOptions.slickOptions.dotsClass"
                        :draggable="normalizedOptions.slickOptions.draggable"
                        :edgeFriction="normalizedOptions.slickOptions.edgeFriction"
                        :fade="normalizedOptions.slickOptions.fade"
                        :focusOnSelect="normalizedOptions.slickOptions.focusOnSelect"
                        :infinite="normalizedOptions.slickOptions.infinite"
                        :initialSlide="normalizedOptions.slickOptions.initialSlide"
                        :lazyLoad="normalizedOptions.slickOptions.lazyLoad"
                        :pauseOnDotsHover="normalizedOptions.slickOptions.pauseOnDotsHover"
                        :pauseOnFocus="normalizedOptions.slickOptions.pauseOnFocus"
                        :pauseOnHover="normalizedOptions.slickOptions.pauseOnHover"
                        :responsive="normalizedOptions.slickOptions.responsive"
                        :rows="normalizedOptions.slickOptions.rows"
                        :rtl="normalizedOptions.slickOptions.rtl"
                        :slidesPerRow="normalizedOptions.slickOptions.slidesPerRow"
                        :slidesToScroll="normalizedOptions.slickOptions.slidesToScroll"
                        :slidesToShow="normalizedOptions.slickOptions.slidesToShow"
                        :speed="normalizedOptions.slickOptions.speed"
                        :swipe="normalizedOptions.slickOptions.swipe"
                        :swipeToSlide="normalizedOptions.slickOptions.swipeToSlide"
                        :touchMove="normalizedOptions.slickOptions.touchMove"
                        :touchThreshold="normalizedOptions.slickOptions.touchThreshold"
                        :useCSS="normalizedOptions.slickOptions.useCSS"
                        :useTransform="normalizedOptions.slickOptions.useTransform"
                        :variableWidth="normalizedOptions.slickOptions.variableWidth"
                        :vertical="normalizedOptions.slickOptions.vertical"
                        :waitForAnimate="normalizedOptions.slickOptions.waitForAnimate"
                    >
                        <template
                            #prevArrow="arrowOption"
                            v-if="normalizedOptions.slickOptions.prevArrowHTML"
                        >
                            <div v-html="normalizedOptions.slickOptions.prevArrowHTML"></div>
                        </template>
                        <template
                            #nextArrow="arrowOption"
                            v-if="normalizedOptions.slickOptions.nextArrowHTML"
                        >
                            <div v-html="normalizedOptions.slickOptions.nextArrowHTML"></div>
                        </template>
                        <div
                            v-for="listing in sliderListings"
                            :key="listing.id"
                            :class="normalizedOptions.slideItem.classes"
                        >
                            <div :style="normalizedOptions.slideItem.style">
                                <listing-card
                                    :listing="listing"
                                    :show-link-to-listing="normalizedOptions.showLinkToListing"
                                    :link-to-listing="getLink(listing)"
                                    :options="normalizedOptions.listingCardOptions"
                                />
                            </div>
                        </div>
                    </VueSlickCarousel>
                </v-col>
                <v-col cols="12" v-else>
                    <v-slide-group
                        center-active
                        show-arrows
                    >
                        <v-slide-item
                            v-for="listing in sliderListings"
                            :key="listing.id"
                            :class="normalizedOptions.slideItem.classes"
                        >
                            <div :style="normalizedOptions.slideItem.style">
                                <listing-card
                                    :listing="listing"
                                    :show-link-to-listing="normalizedOptions.showLinkToListing"
                                    :link-to-listing="getLink(listing)"
                                    :options="normalizedOptions.listingCardOptions"
                                />
                            </div>
                        </v-slide-item>
                    </v-slide-group>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import {defineComponent, computed, onMounted, ref} from '@vue/composition-api';
import {merge, has, map, omitBy, isNil} from "lodash";
import VueSlickCarousel from 'vue-slick-carousel'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'
// optional style for arrows & dots
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css'
import ListingCard from "./ListingCard";
import ListingService from "../services/ListingService";




export default defineComponent({
    name: 'ListingCardSlider',
    components: {
        ListingCard,
        VueSlickCarousel
    },
    props: {
        listings: {
            type: Array,
            default: () => ([])
        },
        endpoint: {
            type: String,
            default: null
        },
        settings: {
            type: Object
        }
    },
    setup(props) {
        const loading = ref(false);
        const renderComponent = ref(false);
        const defaultSettings = ref({
            type: 'slick',
            linkToListing: '/listings/{slug}',
            showLinkToListing: true,
            slideItem: {
                classes: 'pa-4',
                style: '',
            },
            listingCardOptions: {
                fillHeight: true,
                landscape: false,
                quantityIcons: false,
                propertyTypeIcons: true,
                iconLibrary: 'fontawesome',
                hideLogos: false,
                stats: {
                    bedrooms: {shown: true},
                    bathrooms: {shown: true},
                    deposit: {shown: true},
                    sharePercentage: {shown: false},
                    share: {shown: true},
                    price: {shown: true, label: 'Full market value'},
                    rent: {shown: true}
                }
            },
            slickOptions: {
                accessibility: true,
                adaptiveHeight: false,
                arrows: true,
                asNavFor: null,
                autoplay: false,
                autoplaySpeed: 3000,
                centerMode: false,
                centerPadding: '50px',
                cssEase: 'ease',
                dots: false,
                dotsClass: 'slick-dots',
                draggable: 	true,
                edgeFriction: 0.35,
                fade: false,
                focusOnSelect: false,
                infinite: true,
                initialSlide: 0,
                lazyLoad: null,
                pauseOnDotsHover: false,
                pauseOnFocus: false,
                pauseOnHover: true,
                responsive: null,
                rows: 1,
                rtl: false,
                slidesPerRow : 1,
                slidesToScroll: 1,
                slidesToShow: 1,
                speed : 500,
                swipe: true,
                swipeToSlide: false,
                touchMove: true,
                touchThreshold: 5,
                useCSS: true,
                useTransform: true,
                variableWidth: false,
                vertical: false,
                waitForAnimate: true,
                prevArrowHTML: null,
                nextArrowHTML: null,
            }
        });

        const sliderListings = ref(null);

        const normalizedOptions = computed(() => {
            return merge(defaultSettings.value, props.settings);
        });

        function getLink(listing) {
            if (normalizedOptions.value.linkToListing && normalizedOptions.value.showLinkToListing) {
                const listingSlug = has(listing, 'widgetSiteSettings.customSlug') && listing.widgetSiteSettings.customSlug ?
                    listing.widgetSiteSettings.customSlug : listing.slug;
                let url = normalizedOptions.value.linkToListing;

                url = url.replace('{slug}', listingSlug);
                return url;
            }
        }

        function init() {
            loading.value = true;
            if (props.listings.length > 0) {
                sliderListings.value = props.listings;
                const logos = map(sliderListings.value, 'logo');
                if(logos.includes(null)) {
                    defaultSettings.value.listingCardOptions.hideLogos = true;
                }
                renderComponent.value = true;
                loading.value = false;
            } else if (props.endpoint) {
                ListingService.getListingsFromUrl(props.endpoint).then((response) => {
                    if (has(response, 'data.data')) {
                        if (response.data.data.length) {
                            sliderListings.value = response.data.data;
                            const logos = map(sliderListings.value, 'logo');
                            if(logos.includes(null)) {
                                defaultSettings.value.listingCardOptions.hideLogos = true;
                            }
                            renderComponent.value = true;
                        }
                    } else {
                        renderComponent.value = false;
                    }
                    loading.value = false;
                }).catch(error => {
                    console.error(error);
                    loading.value = false;
                });
            } else {
                loading.value = false;
                renderComponent.value = false;
            }
        }

        onMounted(() => {
            init();
        });

        return {
            // Variables
            loading,
            renderComponent,
            sliderListings,
            normalizedOptions,
            // Functions
            getLink
        };
    }
});
</script>
<style>
</style>
